import { Msg } from "@terra-money/feather.js";
import { MsgExecuteContract } from "@goblinhunt/cosmes/client";
import { ConfigResponse } from "models/factory/ConfigResponse";
import { RegisteredTokenResponse } from "models/factory/RegisteredTokenResponse";
import { TokenInfoResponse } from "models/token/TokenInfoResponse";
import { useWallet } from "provider/WalletProvider";
import { useEffect, useState } from "react";
import { FACTORY_ADDRESS } from "utils/Constants";
import { fromDenom, paste, toDenom } from "utils/Helpers";
import { querySmartContract } from "utils/RpcHelpers";
import { showLoadingToast, showTxToast } from "utils/Toasts";

export function FeeConfigPage() {
    const { address: walletAddress, post } = useWallet()
    const [tokenInfo, setTokenInfo] = useState<TokenInfoResponse>()
    const [address, setAddress] = useState('');
    const [codeId, setCodeId] = useState('');
    const [createFee, setCreateFee] = useState('');
    const [stakingFee, setStakingFee] = useState('');
    const [unstakingFee, setUnstakingFee] = useState('');
    const [feeWallet, setFeeWallet] = useState('');
    const [feeConfig, setFeeConfig] = useState<ConfigResponse>()

    const loadTokenInfo = async () => {
        try {
            if (address == "") return;
            const tokenInfo = await querySmartContract(address, {
                token_info: {}
            })
            setTokenInfo(tokenInfo)
        } catch (e) {
            setTokenInfo(undefined)
        }
    }

    const loadConfig = async () => {
        try {
            const res: ConfigResponse = await querySmartContract(FACTORY_ADDRESS, { config: {} });
            setFeeConfig(res)
        } catch (e) { }
    }

    const executeSetConfig = async () => {
        let setConfigMsg = {
            set_config: {
                token_contract: address,
                staking_code_id: Number(codeId),
                create_fee: toDenom(Number(createFee), tokenInfo?.decimals ?? 6).toString(),
                staking_fee: toDenom(Number(stakingFee), tokenInfo?.decimals ?? 6).toString(),
                unstaking_fee: toDenom(Number(unstakingFee), tokenInfo?.decimals ?? 6).toString(),
                fee_wallet: feeWallet
            }
        }

        let executeMsg = new MsgExecuteContract({
            sender: walletAddress!,
            contract: FACTORY_ADDRESS,
            msg: setConfigMsg,
            funds: []
        })
        const msgs = [executeMsg]

        let result = await showLoadingToast("Signing transaction", () => post({
            msgs,
        }))

        await showTxToast(result)
        loadConfig()
        loadTokenInfo()
    }

    // const migrateAll = async () => {
    //     const { tokens }: RegisteredTokenResponse = await showLoadingToast("Load all staking contracts", () => querySmartContract(FACTORY_ADDRESS, { registered_token: {} }))

    //     let msgs: Msg[] = tokens.map(e => new MsgMigrateContract(walletAddress!, e.staking_address, Number(codeId), {}))

    //     let result: PostResponse = await showLoadingToast("Signing transaction", () => post({
    //         chainID: "columbus-5",
    //         msgs,
    //         fee,
    //     }))

    //     await showTxToast(result.txhash)
    // }

    useEffect(() => {
        if (feeConfig?.config?.staking_code_id) {
            setCodeId(feeConfig.config.staking_code_id.toString());
        }
        if (feeConfig?.config?.token_contract) {
            setAddress(feeConfig.config.token_contract);
        }
        if (feeConfig?.config?.create_fee) {
            setCreateFee(fromDenom(Number(feeConfig.config.create_fee), tokenInfo?.decimals ?? 6).toString());
        }
        if (feeConfig?.config?.staking_fee) {
            setStakingFee(fromDenom(Number(feeConfig.config.staking_fee), tokenInfo?.decimals ?? 6).toString());
        }
        if (feeConfig?.config?.unstaking_fee) {
            setUnstakingFee(fromDenom(Number(feeConfig.config.unstaking_fee), tokenInfo?.decimals ?? 6).toString());
        }
        if (feeConfig?.config?.fee_wallet) {
            setFeeWallet(feeConfig.config.fee_wallet);
        }
    }, [feeConfig])

    useEffect(() => {
        loadConfig()
    }, [])

    useEffect(() => {
        loadTokenInfo();
    }, [address])

    const isValid = () => {
        if (!walletAddress) return "Please connect your wallet first"

        if (!tokenInfo) return "Cannot load token info, make sure the token address is correct"

        const codeIdNum = Number(codeId)
        if (isNaN(codeIdNum) || codeIdNum % 1 != 0) return "Code ID must be a number and cannot be a decimal"

        const createFeeNum = Number(createFee)
        if (isNaN(createFeeNum) || createFeeNum <= 0) return "Create fee must be more than 0, use . (period) as decimal separator"

        const stakingFeeNum = Number(stakingFee)
        if (isNaN(stakingFeeNum) || stakingFeeNum <= 0) return "Staking fee must be more than 0, use . (period) as decimal separator"

        const unstakingFeeNum = Number(unstakingFee)
        if (isNaN(unstakingFeeNum) || unstakingFeeNum <= 0) return "Unstaking fee must be more than 0, use . (period) as decimal separator"

        if (!feeWallet) return "Please enter the fee wallet"
    }

    return <div className="container-fluid flex-grow-1 container-p-y">
        <div className="row">
            <div className="col-md-6 col-12 mb-md-0 mb-4">
                <div className="card">
                    <h5 className="card-header">Token Info</h5>
                    <div className="card-body">
                        <div className="mb-3">
                            <label htmlFor="token-address" className="form-label">Address</label>
                            <div className="input-group">
                                <input
                                    value={address}
                                    onChange={({ target }) => setAddress(target.value)}
                                    id="token-address"
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter CW20 token address"
                                />
                                <button onClick={() => paste(setAddress)} className="btn btn-outline-primary" type="button">Paste</button>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="token-name" className="form-label">Name</label>
                            <input
                                value={tokenInfo?.name ?? ""}
                                className="form-control"
                                type="text"
                                id="token-name"
                                placeholder="Token name"
                                readOnly={true}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="token-symbol" className="form-label">Symbol</label>
                            <input
                                value={tokenInfo?.symbol ?? ""}
                                className="form-control"
                                type="text"
                                id="token-symbol"
                                placeholder="Token symbol"
                                readOnly={true}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="code-id" className="form-label">Staking Code</label>
                            <div className="input-group">
                                <input
                                    value={codeId}
                                    onChange={({ target }) => setCodeId(target.value)}
                                    id="code-id"
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter Staking Code ID"
                                />
                            </div>
                            <div className="form-text">
                                Enter staking contract code id
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md-6 col-12">
                <div className="card">
                    <h5 className="card-header">Fee Configuration</h5>
                    <div className="card-body">
                        <div className="mb-3">
                            <label htmlFor="config-create-fee" className="form-label">Create Fee</label>
                            <div className="input-group">
                                <input
                                    onChange={({ target }) => setCreateFee(target.value)}
                                    value={createFee}
                                    type="number"
                                    className="form-control"
                                    id="config-create-fee"
                                    placeholder="Enter create fee"
                                />
                                <span className="input-group-text">{tokenInfo?.symbol}</span>
                            </div>
                            <div className="form-text">
                                Enter the fee for register token
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="staking-apr" className="form-label">Staking Fee</label>
                            <div className="input-group">
                                <input
                                    onChange={({ target }) => setStakingFee(target.value)}
                                    value={stakingFee}
                                    min="1"
                                    step="1"
                                    type="number"
                                    className="form-control"
                                    id="staking-apr"
                                    placeholder="Enter staking fee"
                                />
                                <span className="input-group-text">{tokenInfo?.symbol}</span>
                            </div>
                            <div className="form-text">
                                Enter the fee for staking
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="config-unstake-fee" className="form-label">Unstaking Fee</label>
                            <div className="input-group">
                                <input
                                    value={unstakingFee}
                                    onChange={({ target }) => setUnstakingFee(target.value)}
                                    id="config-unstake-fee"
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter unstaking fee"
                                />
                                <span className="input-group-text">{tokenInfo?.symbol}</span>
                            </div>
                            <div className="form-text">
                                Enter the fee for unstaking
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="fee-wallet" className="form-label">Fee Wallet</label>
                            <div className="input-group">
                                <input
                                    value={feeWallet}
                                    onChange={({ target }) => setFeeWallet(target.value)}
                                    id="fee-wallet"
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter wallet address"
                                />
                            </div>
                            <div className="form-text">
                                Enter the wallet address to recieve the fee
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-12 mt-4">
                <div className="card">
                    <h5 className="card-header">Change Configuration</h5>
                    <div className="card-body">
                        {
                            isValid() && <div className="mb-3 col-12 mb-0">
                                <div className="alert alert-danger">
                                    <p className="mb-0">
                                        {isValid()}
                                    </p>
                                </div>
                            </div>
                        }


                        <button
                            disabled={isValid() != undefined}
                            onClick={() => executeSetConfig()}
                            className="btn btn-primary">Save Configuration</button>

                        <div className="mt-3"></div>

                        {/* <button
                            disabled={isValid() != undefined}
                            onClick={() => migrateAll()}
                            className="btn btn-outline-primary">Migrate All</button> */}
                    </div>
                </div>
            </div>
        </div>
    </div>
}